import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import BlogCard from '@/components/blog/BlogCard';
import { Calendar, Clock, Share2, Twitter, Linkedin, Facebook, Link, ArrowLeft } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

const BlogPost = () => {
  const { slug } = useParams();

  // Mock data - replace with real data from your API/CMS
  const post = {
    title: 'Comment construire une startup tech qui dure ?',
    excerpt: 'Découvrez les étapes clés pour bâtir une startup technologique pérenne, de la validation de l\'idée au développement du MVP.',
    content: `
      <h2>Validation de l'idée : la première étape cruciale</h2>
      <p>Avant de se lancer tête baissée dans le développement, il est essentiel de valider son idée auprès du marché. Cette étape permet d'éviter de construire un produit que personne ne souhaite utiliser. Voici les points clés à considérer :</p>
      <ul>
        <li>Identifier clairement le problème que vous souhaitez résoudre</li>
        <li>Définir votre cible avec précision</li>
        <li>Réaliser des interviews utilisateurs</li>
        <li>Analyser la concurrence existante</li>
      </ul>

      <h2>Construire un MVP pertinent</h2>
      <p>Le MVP (Minimum Viable Product) est une version simplifiée mais fonctionnelle de votre produit. Il doit permettre de :</p>
      <ul>
        <li>Tester vos hypothèses principales</li>
        <li>Obtenir des retours utilisateurs rapidement</li>
        <li>Itérer sur les fonctionnalités essentielles</li>
      </ul>

      <blockquote>
        "Le secret d'une startup réussie n'est pas de créer le produit parfait du premier coup, mais d'itérer rapidement en se basant sur les retours utilisateurs."
      </blockquote>

      <h2>L'importance d'une architecture technique solide</h2>
      <p>Une base technique solide est essentielle pour supporter la croissance de votre startup. Voici les points à ne pas négliger :</p>
      <ul>
        <li>Choisir des technologies éprouvées et scalables</li>
        <li>Mettre en place une architecture modulaire</li>
        <li>Automatiser les tests et le déploiement</li>
        <li>Prévoir la montée en charge</li>
      </ul>

      <h2>Constituer une équipe performante</h2>
      <p>Le succès d'une startup repose en grande partie sur la qualité de son équipe. Voici nos conseils pour constituer une équipe performante :</p>
      <ul>
        <li>Privilégier la complémentarité des compétences</li>
        <li>Cultiver une culture d'entreprise forte</li>
        <li>Mettre en place des processus de communication efficaces</li>
        <li>Encourager l'innovation et l'initiative</li>
      </ul>
    `,
    image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=2070&auto=format&fit=crop',
    category: 'Startup',
    date: '15 Mars 2024',
    readTime: '8 min',
    author: {
      name: 'Malik Nassourou',
      role: 'Fondateur & CTO',
      avatar: ''
    }
  };

  const relatedPosts = [
    {
      id: '2',
      title: 'Les technologies à surveiller en 2024',
      excerpt: 'Notre analyse des technologies émergentes qui vont façonner le paysage numérique cette année.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop',
      category: 'Tech',
      date: '10 Mars 2024',
      readTime: '6 min'
    },
    {
      id: '3',
      title: 'React vs Vue : Quel framework choisir en 2024 ?',
      excerpt: 'Analyse comparative approfondie pour vous aider à choisir le meilleur framework pour votre projet.',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=2070&auto=format&fit=crop',
      category: 'Développement',
      date: '8 Mars 2024',
      readTime: '10 min'
    }
  ];

  return (
    <>
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, startup, tech, innovation, développement, entrepreneuriat`}
        type="article"
        image={post.image}
        author={post.author.name}
        publishedTime={new Date(post.date).toISOString()}
        section={post.category}
        url={`https://innov.orcsys.fr/blog/${slug}`}
      />
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[70vh] min-h-[600px] flex items-center">
          {/* Background Image with Pattern Overlay */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/95 via-slate-900/80 to-slate-900/95 z-10" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+CiAgPHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPgogIDxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMCA0IDBhMiAyIDAwIDEgMCAtNCAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz4KPC9zdmc+')] opacity-30 z-20" />
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="container px-4 mx-auto relative z-30">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto text-center"
            >
              <RouterLink 
                to="/blog"
                className="inline-flex items-center text-white/90 hover:text-white mb-8 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au blog
              </RouterLink>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-block px-4 py-1.5 bg-orcsys-500/90 backdrop-blur-sm text-white text-sm font-medium rounded-full mb-6">
                  {post.category}
                </span>
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
                {post.title}
              </h1>

              <div className="inline-flex items-center space-x-6 text-white/90 bg-slate-900/50 backdrop-blur-sm px-6 py-3 rounded-full">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {post.date}
                </div>
                <div className="h-4 w-px bg-white/20" />
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  {post.readTime} de lecture
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 lg:py-20">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto">
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-orcsys-500 prose-a:no-underline hover:prose-a:text-orcsys-600 prose-blockquote:border-l-4 prose-blockquote:border-orcsys-500 prose-blockquote:bg-slate-50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-strong:text-slate-900"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-center justify-center space-x-4 mt-12 pt-12 border-t border-slate-200"
              >
                <span className="text-slate-600 font-medium">Partager :</span>
                <div className="flex items-center space-x-3">
                  <button className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    <Twitter className="w-5 h-5 text-slate-600" />
                  </button>
                  <button className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    <Linkedin className="w-5 h-5 text-slate-600" />
                  </button>
                  <button className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    <Facebook className="w-5 h-5 text-slate-600" />
                  </button>
                  <button className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    <Link className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              </motion.div>

              {/* Author Section - Moved to bottom */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-12 pt-12 border-t border-slate-200"
              >
                <div className="bg-slate-50 rounded-2xl p-8">
                  <div className="flex items-center space-x-6">
                    <img
                      src={post.author.avatar}
                      alt={post.author.name}
                      className="w-20 h-20 rounded-full object-cover ring-4 ring-white"
                    />
                    <div>
                      <h3 className="font-bold text-xl text-slate-900 mb-1">{post.author.name}</h3>
                      <p className="text-slate-600 mb-4">{post.author.role}</p>
                      <p className="text-slate-700">
                        Expert en développement web et mobile avec plus de 6 ans d'expérience en programmation.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 lg:py-20 bg-slate-50">
          <div className="container px-4 mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-slate-900 mb-12 text-center"
            >
              Articles similaires
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {relatedPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <BlogCard post={post} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPost; 